import styles from './TermsOfService.module.css';

const SECTIONS = [
    {
        id: 'acceptance',
        title: '1. Acceptance of terms',
        content: [
            'By accessing or using Code Venture Labs ("the Platform"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Platform.',
            'We reserve the right to update these terms at any time. We will notify you of significant changes via email or a notice on the Platform. Your continued use after such changes constitutes acceptance of the new terms.',
        ],
    },
    {
        id: 'account',
        title: '2. Your account',
        content: [
            'You must be at least 16 years old to create an account. By registering, you confirm that the information you provide is accurate and complete.',
            'You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. Notify us immediately at legal@codeventurelabs.dev if you suspect unauthorised access.',
            'We reserve the right to suspend or terminate accounts that violate these terms or that have been inactive for more than 24 months.',
        ],
    },
    {
        id: 'content',
        title: '3. Course content & intellectual property',
        content: [
            'All course content, including written lessons, code examples, quizzes, and assessments, is the intellectual property of Code Venture Labs or its licensed contributors and is protected by copyright law.',
            'Your account grants you a personal, non-transferable licence to access and view course content for your own learning purposes. You may not reproduce, distribute, resell, or publicly display any content without our prior written consent.',
            'Code you write yourself during exercises is yours. We make no claim of ownership over your original work.',
        ],
    },
    {
        id: 'conduct',
        title: '4. Acceptable use',
        content: [
            'You agree not to use the Platform to: share your account credentials with others; scrape, download, or systematically copy course content; upload or transmit malicious code; harass, abuse, or harm other users; or attempt to gain unauthorised access to our systems.',
            'Violation of these rules may result in immediate account suspension or termination.',
        ],
    },
    {
        id: 'free',
        title: '5. Free service',
        content: [
            'Code Venture Labs is currently provided free of charge. There are no subscription fees, and we do not collect any payment information.',
            'If we introduce paid features in the future, we will update these terms and give you clear notice beforehand. You will never be charged without your explicit, prior consent.',
        ],
    },
    {
        id: 'privacy',
        title: '6. Privacy',
        content: [
            'Your use of the Platform is also governed by our Privacy Policy, which is incorporated into these terms by reference. By using Code Venture Labs, you agree to the collection and use of your data, including our use of cookies and analytics (such as PostHog and Google Analytics), as described in that policy.',
        ],
    },
    {
        id: 'disclaimer',
        title: '7. Disclaimer of warranties',
        content: [
            'The Platform is provided "as is" without warranties of any kind, express or implied. We do not guarantee that the Platform will be uninterrupted, error-free, or that defects will be corrected.',
            'Code Venture Labs does not guarantee employment or any specific learning outcome as a result of using the Platform.',
        ],
    },
    {
        id: 'liability',
        title: '8. Limitation of liability',
        content: [
            'To the maximum extent permitted by law, Code Venture Labs shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Platform.',
            'As the Platform is provided free of charge, to the maximum extent permitted by applicable law, Code Venture Labs shall have no liability to you for any loss or damage arising from these terms or your use of the Platform. Nothing in these terms excludes or limits any liability that cannot be excluded or limited under applicable law.',
        ],
    },
    {
        id: 'governing',
        title: '9. Governing law',
        content: [
            'These terms are governed by and construed in accordance with the laws of the Grand Duchy of Luxembourg, without regard to conflict of law principles.',
            'Any disputes arising under these terms shall be subject to the exclusive jurisdiction of the competent courts of Luxembourg City, Grand Duchy of Luxembourg.',
        ],
    },
    {
        id: 'contact',
        title: '10. Contact',
        content: [
            'If you have any questions about these Terms of Service, please contact us at legal@codeventurelabs.dev.',
        ],
    },
];

export default function TermsOfService() {
    const scrollTo = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div className={`${styles.page} container` }>

            {/* ── Hero ── */}
            <div className={styles.hero}>
                <div className={styles.heroInner}>
                    <span className={styles.eyebrow}>Legal</span>
                    <h1 className={styles.heroHeading}>Terms of Service</h1>
                    <div className={styles.heroRule} />
                    <p className={styles.heroMeta}>
                        Last updated <strong>20 June 2026</strong> · Effective immediately
                    </p>
                    <p className={styles.heroLead}>
                        Please read these terms carefully before using Code Venture Labs. They explain your rights,
                        our responsibilities, and what we both agree to when you use the Platform.
                    </p>
                </div>
            </div>

            <div className={styles.layout}>

                {/* ── Sidebar TOC ── */}
                <aside className={styles.toc}>
                    <p className={styles.tocLabel}>On this page</p>
                    <nav>
                        {SECTIONS.map((s) => (
                            <button
                                key={s.id}
                                className={styles.tocLink}
                                onClick={() => scrollTo(s.id)}
                            >
                                {s.title}
                            </button>
                        ))}
                    </nav>
                </aside>

                {/* ── Content ── */}
                <article className={styles.content}>
                    {SECTIONS.map((s) => (
                        <section key={s.id} id={s.id} className={styles.section}>
                            <h2 className={styles.sectionTitle}>{s.title}</h2>
                            {s.content.map((para, i) => (
                                <p key={i} className={styles.para}>{para}</p>
                            ))}
                        </section>
                    ))}

                    <div className={styles.acceptance}>
                        <i className="fas fa-circle-check" />
                        <p>
                            By creating an account or continuing to use Code Venture Labs, you acknowledge
                            that you have read and agree to these Terms of Service.
                        </p>
                    </div>
                </article>

            </div>
        </div>
    );
}
